import React, { useState } from "react";
import cn from "classnames";
import styles from "./MessageThread.module.sass";
import Icon from "../../Icon";
import { hasCollabIntent } from "../../../utils/collabDecline";

/**
 * Linkify message text - converts URLs into clickable <a> tags.
 * Supports:
 *   - http:// and https:// URLs
 *   - www.* URLs (auto-prefix https://)
 *   - Bare domain URLs like example.com/path (auto-prefix https://)
 * Strips common trailing punctuation that likely isn't part of the URL.
 */
const renderMessageText = (text) => {
    if (!text) return null;

    // Regex to match URLs:
    // 1. Explicit http(s)://...
    // 2. www....
    // 3. Bare domains (word.tld optionally followed by path/query)
    // Common TLDs for bare domain matching
    const tlds = 'com|org|net|io|co|app|dev|ai|edu|gov|info|biz|me|us|uk|ca|au|de|fr|es|it|nl|be|ch|at|jp|kr|cn|in|br|mx|ru|za|nz|se|no|fi|dk|pl|pt|cz|hu|ro|gr|tr|il|ae|sg|hk|tw|ph|my|id|th|vn|travel|xyz|online|store|shop|tech|site|club|blog|art|design|agency|media|news|tv|fm|gg|ly|to';
    
    // URL pattern that captures the URL and any trailing punctuation
    const urlPattern = new RegExp(
        `(https?:\\/\\/[^\\s<>]+|www\\.[^\\s<>]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.(?:${tlds})(?:\\/[^\\s<>]*)?)`,
        'gi'
    );

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = urlPattern.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        let url = match[0];
        let trailingPunctuation = '';

        // Strip common trailing punctuation that's unlikely to be part of the URL
        // but preserve balanced parentheses
        const trailingMatch = url.match(/([.,;:!?)\]}>'"]+)$/);
        if (trailingMatch) {
            const trailing = trailingMatch[1];
            // Count open vs close parens in URL to handle URLs like (example.com)
            const openParens = (url.match(/\(/g) || []).length;
            const closeParens = (url.match(/\)/g) || []).length;
            
            // If there are more closing parens than opening, the trailing ) is likely not part of URL
            let charsToStrip = 0;
            for (let i = trailing.length - 1; i >= 0; i--) {
                const char = trailing[i];
                if (char === ')' && closeParens > openParens) {
                    charsToStrip++;
                } else if ('.,;:!?\'"}>]'.includes(char)) {
                    charsToStrip++;
                } else {
                    break;
                }
            }
            
            if (charsToStrip > 0) {
                trailingPunctuation = url.slice(-charsToStrip);
                url = url.slice(0, -charsToStrip);
            }
        }

        // Build the href - ensure it has a protocol
        let href = url;
        if (!/^https?:\/\//i.test(href)) {
            href = 'https://' + href;
        }

        // Validate the URL is safe (only http/https protocols)
        try {
            const parsed = new URL(href);
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
                // Not a safe protocol, render as plain text
                parts.push(match[0]);
                lastIndex = match.index + match[0].length;
                continue;
            }
        } catch {
            // Invalid URL, render as plain text
            parts.push(match[0]);
            lastIndex = match.index + match[0].length;
            continue;
        }

        // Add the link
        parts.push(
            <a
                key={match.index}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
            >
                {url}
            </a>
        );

        // Add back any trailing punctuation as plain text
        if (trailingPunctuation) {
            parts.push(trailingPunctuation);
        }

        lastIndex = match.index + match[0].length;
    }

    // Add any remaining text after the last match
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

const Message = ({ 
    message, 
    isOwn, 
    showAvatar,
    isFirstInGroup,
    isLastInGroup,
    onReply,
    onEdit,
    onDelete,
    onDecline,
    onAcceptCollab,
    onDeclineCollab,
    currentUserId
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const {
        text,
        timestamp,
        isRead,
        isDelivered,
        sender,
        replyTo,
        attachments,
        isEdited,
        isSystem,
        isCollabRequest,
        collabRequestId,
        collabRequestSnapshot,
        collabRequestRecipientId,
        collabDeclineResponse,
        collabAcceptResponse
    } = message;
    
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        });
    };
    
    const handleDownloadAttachment = async (attachment, e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!attachment.url) {
            console.error('No URL found for attachment:', attachment);
            return;
        }
        
        try {
            // For images, open in new tab
            if (attachment.type === 'image') {
                window.open(attachment.url, '_blank', 'noopener,noreferrer');
                return;
            }
            
            // For files (PDFs, documents, etc.), trigger download
            // Fetch the file and create a blob URL for download
            const response = await fetch(attachment.url, {
                method: 'GET',
                headers: {
                    // Don't set any custom headers that might cause CORS issues
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Get the file extension from the URL or MIME type
            let filename = attachment.name || 'attachment';
            if (!filename.includes('.')) {
                // Try to determine extension from MIME type
                const mimeType = attachment.mime_type || blob.type;
                if (mimeType === 'application/pdf') {
                    filename += '.pdf';
                } else if (mimeType.includes('word')) {
                    filename += '.docx';
                } else if (mimeType.includes('text')) {
                    filename += '.txt';
                }
            }
            
            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }, 100);
        } catch (error) {
            console.error('Failed to download attachment:', error);
            // Fallback: try opening in new tab
            // This works for PDFs and other files that browsers can display
            window.open(attachment.url, '_blank', 'noopener,noreferrer');
        }
    };
    
    if (isSystem) {
        return (
            <div className={styles.systemMessage}>
                <span>{text}</span>
            </div>
        );
    }

    // Check if current user is the recipient of this collab request
    // Stream user IDs have prefixes like "creator-" or "host-" so we need to compare the base UUID
    const extractBaseUserId = (streamId) => {
        if (!streamId) return null;
        // Remove common prefixes with both dash and underscore variants
        return streamId.replace(/^(creator|host|traveler|admin|user)[-_]/, '');
    };
    const currentUserBaseId = extractBaseUserId(currentUserId);
    const recipientBaseId = extractBaseUserId(collabRequestRecipientId);
    const isCollabRecipient = isCollabRequest && currentUserBaseId && recipientBaseId && 
        (currentUserBaseId === recipientBaseId || currentUserId === collabRequestRecipientId);

    // Handle accept collab request
    const handleAcceptCollab = async () => {
        if (!onAcceptCollab || !collabRequestId || isAccepting) return;
        setIsAccepting(true);
        try {
            await onAcceptCollab(collabRequestId, message);
        } finally {
            setIsAccepting(false);
        }
    };

    // Handle decline collab request
    const handleDeclineCollab = async () => {
        if (!onDeclineCollab || !collabRequestId || isDeclining) return;
        setIsDeclining(true);
        try {
            await onDeclineCollab(collabRequestId, message);
        } finally {
            setIsDeclining(false);
        }
    };

    // Render a special Collab Request card for explicit collab requests
    if (isCollabRequest) {
        const snapshot = collabRequestSnapshot || {};
        
        return (
            <div className={cn(styles.messageWrapper, styles.collabRequestWrapper, {
                [styles.own]: isOwn,
                [styles.firstInGroup]: isFirstInGroup,
                [styles.lastInGroup]: isLastInGroup
            })}>
                {showAvatar && (
                    <img 
                        src={sender?.avatar || "/images/content/avatar-1.jpg"} 
                        alt={sender?.name}
                        className={styles.avatar}
                    />
                )}
                
                <div className={styles.messageContainer}>
                    {showAvatar && !isOwn && (
                        <div className={styles.senderName}>{sender?.name}</div>
                    )}
                    
                    <div className={cn(styles.messageBubble, styles.collabRequestCard)}>
                        {/* Collab Request Header */}
                        <div className={styles.collabRequestHeader}>
                            <Icon name="briefcase" size="18" />
                            <span className={styles.collabRequestTitle}>Collaboration Request</span>
                        </div>
                        
                        {/* Request Message */}
                        {text && (
                            <p className={styles.messageText}>{renderMessageText(text)}</p>
                        )}
                        
                        {/* Snapshot Details (if available) */}
                        {snapshot && Object.keys(snapshot).length > 0 && (
                            <div className={styles.collabRequestDetails}>
                                {snapshot.dates && (
                                    <div className={styles.collabRequestDetail}>
                                        <Icon name="calendar" size="14" />
                                        <span>
                                            {snapshot.dates.type === 'flexible' 
                                                ? 'Flexible dates' 
                                                : snapshot.dates.start && snapshot.dates.end
                                                    ? `${new Date(snapshot.dates.start).toLocaleDateString()} - ${new Date(snapshot.dates.end).toLocaleDateString()}`
                                                    : 'Dates TBD'}
                                        </span>
                                    </div>
                                )}
                                {snapshot.compensation && (
                                    <div className={styles.collabRequestDetail}>
                                        <Icon name="coin" size="14" />
                                        <span>
                                            {snapshot.compensation.type === 'paid_partnership' ? 'Paid Partnership' :
                                             snapshot.compensation.type === 'hosted_stay' ? 'Hosted Stay' :
                                             snapshot.compensation.type === 'negotiable' ? 'Negotiable' :
                                             snapshot.compensation.type || 'Compensation TBD'}
                                        </span>
                                    </div>
                                )}
                                {snapshot.deliverables && snapshot.deliverables.items?.length > 0 && (
                                    <div className={styles.collabRequestDetail}>
                                        <Icon name="document" size="14" />
                                        <span>{snapshot.deliverables.items.length} deliverable{snapshot.deliverables.items.length > 1 ? 's' : ''}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Timestamp */}
                        <div className={styles.messageFooter}>
                            <span className={styles.timestamp}>
                                {formatTime(timestamp)}
                            </span>
                            {isOwn && (
                                <span className={styles.status}>
                                    {isRead ? (
                                        <Icon name="check-double" size="14" />
                                    ) : isDelivered ? (
                                        <Icon name="check" size="14" />
                                    ) : (
                                        <Icon name="clock" size="14" />
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    {/* Accept/Decline buttons - only for recipient */}
                    {isCollabRecipient && onAcceptCollab && onDeclineCollab && (
                        <div className={styles.collabRequestActions}>
                            <button 
                                className={cn(styles.collabAcceptBtn, { [styles.loading]: isAccepting })}
                                onClick={handleAcceptCollab}
                                disabled={isAccepting || isDeclining}
                            >
                                <Icon name="check" size="14" />
                                {isAccepting ? 'Exploring...' : 'Explore Collab'}
                            </button>
                            <button 
                                className={cn(styles.collabDeclineBtn, { [styles.loading]: isDeclining })}
                                onClick={handleDeclineCollab}
                                disabled={isAccepting || isDeclining}
                            >
                                <Icon name="close" size="14" />
                                {isDeclining ? 'Declining...' : 'Decline'}
                            </button>
                        </div>
                    )}
                    
                    {/* Show "Pending" status for requester */}
                    {isOwn && (
                        <div className={styles.collabRequestStatus}>
                            <Icon name="clock" size="14" />
                            <span>Pending response</span>
                        </div>
                    )}
                </div>
                
                {!showAvatar && !isOwn && (
                    <div className={styles.avatarPlaceholder} />
                )}
            </div>
        );
    }
    
    return (
        <div className={cn(styles.messageWrapper, {
            [styles.own]: isOwn,
            [styles.firstInGroup]: isFirstInGroup,
            [styles.lastInGroup]: isLastInGroup
        })}>
            {showAvatar && (
                <img 
                    src={sender?.avatar || "/images/content/avatar-1.jpg"} 
                    alt={sender?.name}
                    className={styles.avatar}
                />
            )}
            
            <div className={styles.messageContainer}>
                {showAvatar && !isOwn && (
                    <div className={styles.senderName}>{sender?.name}</div>
                )}
                
                {replyTo && (
                    <div className={styles.replyPreview}>
                        <div className={styles.replyBar} />
                        <div className={styles.replyContent}>
                            <div className={styles.replyAuthor}>{replyTo.sender}</div>
                            <div className={styles.replyText}>{replyTo.text}</div>
                        </div>
                    </div>
                )}
                
                <div 
                    className={styles.messageBubble}
                    onMouseEnter={() => setShowMenu(true)}
                    onMouseLeave={() => setShowMenu(false)}
                >
                    {attachments && attachments.length > 0 && (
                        <div className={styles.attachments}>
                            {attachments.map((attachment, index) => (
                                <div key={index} className={styles.attachment}>
                                    {attachment.type === 'image' ? (
                                        <img 
                                            src={attachment.url} 
                                            alt={attachment.name || 'Image attachment'}
                                            className={styles.attachmentImage}
                                            onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                                            title="Click to view full size"
                                        />
                                    ) : (
                                        <div 
                                            className={styles.attachmentFile}
                                            onClick={(e) => handleDownloadAttachment(attachment, e)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Icon name="document" size="20" />
                                            <span>{attachment.name || 'Attachment'}</span>
                                            <button 
                                                className={styles.downloadBtn}
                                                onClick={(e) => handleDownloadAttachment(attachment, e)}
                                                title="Download attachment"
                                            >
                                                <Icon name="download" size="16" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <p className={styles.messageText}>{renderMessageText(text)}</p>
                    
                    <div className={styles.messageFooter}>
                        <span className={styles.timestamp}>
                            {formatTime(timestamp)}
                        </span>
                        {isOwn && (
                            <span className={styles.status}>
                                {isRead ? (
                                    <Icon name="check-double" size="14" />
                                ) : isDelivered ? (
                                    <Icon name="check" size="14" />
                                ) : (
                                    <Icon name="clock" size="14" />
                                )}
                            </span>
                        )}
                    </div>
                    
                    {showMenu && (
                        <div className={styles.messageMenu}>
                            <button 
                                className={styles.menuBtn}
                                onClick={() => onReply(message)}
                                title="Reply"
                            >
                                <Icon name="corner-up-left" size="16" />
                            </button>
                            {isOwn && (
                                <>
                                    <button 
                                        className={styles.menuBtn}
                                        onClick={() => onEdit(message)}
                                        title="Edit"
                                    >
                                        <Icon name="edit" size="16" />
                                    </button>
                                    <button 
                                        className={styles.menuBtn}
                                        onClick={() => onDelete(message)}
                                        title="Delete"
                                    >
                                        <Icon name="trash" size="16" />
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Decline collaboration button - visible for collab requests, but NOT for decline or accept response messages */}
                {!isOwn && hasCollabIntent(text) && !collabDeclineResponse && !collabAcceptResponse && onDecline && (
                    <button 
                        className={styles.declineCollabBtn}
                        onClick={() => onDecline(message)}
                    >
                        <Icon name="close-circle" size="14" />
                        Decline Collaboration?
                    </button>
                )}
            </div>
            
            {!showAvatar && !isOwn && (
                <div className={styles.avatarPlaceholder} />
            )}
        </div>
    );
};

export default Message;
